import type React from "react";
import { BasketballIcon } from "@/svgs/basketball-icon";
import { ClimbingIcon } from "@/svgs/climbing-icon";
import { CricketIcon } from "@/svgs/cricket-icon";
import { CyclingIcon } from "@/svgs/cycling-icon";
import { FightingIcon } from "@/svgs/fighting-icon";
import {
  DayRetreatsIcon,
  DiningIcon,
  FlatAnimalFlowIcon,
  FlatBoulderingIcon,
  FlatHotYogaIcon,
  FlatNutritionIcon,
  FlatScubaDivingIcon,
  FlatSnorkelingIcon,
  FlatStretchingIcon,
  FlatTrekkingIcon,
  GigsIcon,
  GroupTripsIcon,
  KettlebellIcon,
  LongevityIcon,
  NetworkingIcon,
  PaintingIcon,
  PickleballIcon,
  PranayamaIcon,
  RecoveryIcon,
  SalsaIcon,
  SkiingIcon,
  SpinningIcon,
  TravelIcon,
  WellnessIcon,
  YogaPhilosophyIcon,
} from "@/svgs/flat-activity-icons";
import { FootballIcon } from "@/svgs/football-icon";
import { GolfIcon } from "@/svgs/golf-icon";
import { GroupOfPeopleIcon } from "@/svgs/group-of-people-icon";
import { HIITIcon } from "@/svgs/hiit-icon";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";
import { HyroxIcon } from "@/svgs/hyrox-icon";
import { JuiceIcon } from "@/svgs/juice-icon";
import { MeditationIcon } from "@/svgs/meditation-icon";
import { PaddleIcon } from "@/svgs/paddle-icon";
import { PilatesIcon } from "@/svgs/pilates-icon";
import { RunningManIcon } from "@/svgs/running-man-icon";
import { ScubaIcon } from "@/svgs/scuba-icon";
import { ShuttlecockIcon } from "@/svgs/shuttlecock-icon";
import { SnookerIcon } from "@/svgs/snooker-icon";
import { SquashIcon } from "@/svgs/squash-icon";
import { StretchIcon } from "@/svgs/strech-icon";
import { SurfingIcon } from "@/svgs/surfing-icon";
import { SwimmingIcon } from "@/svgs/swimming-icon";
import { TableTennisIcon } from "@/svgs/table-tennis-icon";
import { TennisBallIcon } from "@/svgs/tennis-ball-icon";
import { ThrowballIcon } from "@/svgs/throwball-icon";
import { VolleyballIcon } from "@/svgs/volleyball-icon";
import { WalkingIcon } from "@/svgs/walking-icon";
import { WeightLiftingIcon } from "@/svgs/weight-lifting-icon";
import { WineIcon } from "@/svgs/wine-icon";

export type IconType =
  | "running-man"
  | "walking"
  | "group-of-people"
  | "meditation"
  | "pilates"
  | "shuttlecock"
  | "cricket"
  | "golf"
  | "paddle"
  | "swimming"
  | "tennis-ball"
  | "scuba"
  | "snooker"
  | "surfing"
  | "fighting"
  | "weight-lifting"
  | "juice"
  | "wine"
  | "hiive-logo"
  | "basketball"
  | "football"
  | "volleyball"
  | "climbing"
  | "cycling"
  | "squash"
  | "table-tennis"
  | "throwball"
  | "hyrox"
  | "stretch"
  | "hiit"
  | "hot-yoga"
  | "nutrition"
  | "scuba-diving"
  | "snorkeling"
  | "trekking"
  | "animal-flow"
  | "bouldering"
  | "stretching"
  | "longevity"
  | "networking"
  | "wellness"
  | "painting"
  | "dining"
  | "group-trips"
  | "travel"
  | "skiing"
  | "gigs"
  | "salsa"
  | "kettlebell"
  | "pranayama"
  | "yoga-philosophy"
  | "spinning"
  | "recovery"
  | "day-retreats"
  | "pickleball";

const iconMap: Record<
  IconType,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  "running-man": RunningManIcon,
  walking: WalkingIcon,
  "group-of-people": GroupOfPeopleIcon,
  meditation: MeditationIcon,
  pilates: PilatesIcon,
  shuttlecock: ShuttlecockIcon,
  cricket: CricketIcon,
  golf: GolfIcon,
  paddle: PaddleIcon,
  swimming: SwimmingIcon,
  "tennis-ball": TennisBallIcon,
  scuba: ScubaIcon,
  snooker: SnookerIcon,
  surfing: SurfingIcon,
  fighting: FightingIcon,
  "weight-lifting": WeightLiftingIcon,
  juice: JuiceIcon,
  wine: WineIcon,
  "hiive-logo": HiiveLogoIcon,
  basketball: BasketballIcon,
  football: FootballIcon,
  volleyball: VolleyballIcon,
  climbing: ClimbingIcon,
  cycling: CyclingIcon,
  squash: SquashIcon,
  "table-tennis": TableTennisIcon,
  throwball: ThrowballIcon,
  hyrox: HyroxIcon,
  stretch: StretchIcon,
  hiit: HIITIcon,
  "hot-yoga": FlatHotYogaIcon,
  nutrition: FlatNutritionIcon,
  "scuba-diving": FlatScubaDivingIcon,
  snorkeling: FlatSnorkelingIcon,
  trekking: FlatTrekkingIcon,
  "animal-flow": FlatAnimalFlowIcon,
  bouldering: FlatBoulderingIcon,
  stretching: FlatStretchingIcon,
  longevity: LongevityIcon,
  networking: NetworkingIcon,
  wellness: WellnessIcon,
  painting: PaintingIcon,
  dining: DiningIcon,
  "group-trips": GroupTripsIcon,
  travel: TravelIcon,
  skiing: SkiingIcon,
  gigs: GigsIcon,
  salsa: SalsaIcon,
  kettlebell: KettlebellIcon,
  pranayama: PranayamaIcon,
  "yoga-philosophy": YogaPhilosophyIcon,
  spinning: SpinningIcon,
  recovery: RecoveryIcon,
  "day-retreats": DayRetreatsIcon,
  pickleball: PickleballIcon,
};

export const getIconComponent = (iconType: IconType) => {
  return iconMap[iconType] || (() => null);
};

/** All registered icon keys. */
export const ICON_KEYS = Object.keys(iconMap) as IconType[];

/** Icon keys an admin can assign to an activity (excludes brand/logo marks). */
export const ACTIVITY_ICON_KEYS = ICON_KEYS.filter(
  (key) => key !== "hiive-logo",
);
